import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api, readBlobError } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import Button from './ui/Button';
import PaymentDetailsBlock from './PaymentDetailsBlock';

interface PaymentQRModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'event' | 'membership';
  entityId: number;
  entityName: string;
  memberId: number;
  memberName: string;
  amount: number;
  durationDays?: number;
}

const PaymentQRModal: React.FC<PaymentQRModalProps> = ({
  isOpen,
  onClose,
  type,
  entityId,
  entityName,
  memberId,
  memberName,
  amount,
  durationDays,
}) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setQrUrl(null);
      setError(null);
      return;
    }

    const controller = new AbortController();

    const fetchQR = async () => {
      setLoading(true);
      setError(null);
      try {
        // Admins use the gated admin QR routes; members use the policy-gated
        // /my routes (admins are 403'd off /my and vice versa).
        let response;
        if (type === 'event') {
          response = isAdmin
            ? await api.payments.getQR(String(entityId), String(memberId), controller.signal)
            : await api.my.payments.qr(String(entityId), String(memberId), controller.signal);
        } else {
          response = isAdmin
            ? await api.payments.getMembershipQR(String(entityId), String(memberId), durationDays, controller.signal)
            : await api.my.payments.membershipQr(String(entityId), String(memberId), durationDays, controller.signal);
        }
        const blob = new Blob([response.data as BlobPart], { type: 'image/png' });
        const url = URL.createObjectURL(blob);
        setQrUrl(url);
      } catch (err: any) {
        if (controller.signal.aborted) return;
        const { code } = await readBlobError(err);
        if (code === 'BANK_NOT_CONFIGURED') {
          setError(isAdmin ? t('payments.qrBankNotConfiguredAdmin') : t('payments.qrUnavailable'));
        } else if (code === 'PAYMENT_SETTLED') {
          setError(t('payments.qrAlreadyPaid'));
        } else {
          setError(t('payments.qrUnavailable'));
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchQR();

    return () => {
      controller.abort();
    };
  }, [isOpen, type, entityId, memberId, durationDays, isAdmin]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('payments.generateQR')} size="small">
      <div className="qr-modal-content">
        <div className="qr-payment-details">
          <div><strong>{memberName}</strong></div>
          <div>{entityName}</div>
          <div className="qr-amount">{amount.toLocaleString()} CZK</div>
        </div>

        {loading && <div className="qr-loading">{t('common.loading')}</div>}

        {error && <div className="qr-error">{error}</div>}

        {qrUrl && (
          <>
            <div className="qr-image-container">
              <img src={qrUrl} alt="Payment QR Code" className="qr-image" />
            </div>
            <PaymentDetailsBlock
              type={type}
              entityId={entityId}
              memberId={memberId}
              isAdmin={isAdmin}
            />
          </>
        )}

        <div className="qr-modal-actions">
          <Button variant="secondary" fullWidth onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PaymentQRModal;
